import { useState } from 'react';
import { supabase } from './supabaseClient';

export default function ProfileSetup({ user, onComplete }) {
  const [username, setUsername] = useState('');
  const [contactEmail, setContactEmail] = useState(user.email || '');
  const [isPublic, setIsPublic] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    if (!username.trim()) { setError('Username is required.'); return; }
    if (!/^[a-zA-Z0-9_-]+$/.test(username)) {
      setError('Username can only contain letters, numbers, hyphens and underscores.');
      return;
    }
    setLoading(true);
    setError(null);
    const { error: err } = await supabase.from('profiles').insert({
      id: user.id,
      username: username.trim().toLowerCase(),
      contact_email: contactEmail.trim(),
      is_public: isPublic,
    });
    if (err) {
      if (err.code === '23505') setError('That username is already taken. Try another.');
      else setError(err.message);
      setLoading(false);
    } else {
      onComplete();
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.stars} />
      <div style={styles.card}>
        <div style={styles.logoLine}>COLLECTIONKEEPER</div>
        <h1 style={styles.title}>SET UP YOUR <span style={styles.accent}>PROFILE</span></h1>
        <div style={styles.subtitle}>// ONE TIME SETUP //</div>
        <div style={styles.divider} />

        <div style={styles.field}>
          <label style={styles.label}>USERNAME</label>
          <input
            style={styles.input}
            value={username}
            onChange={e => setUsername(e.target.value)}
            placeholder="e.g. tomas_collector"
            maxLength={30}
          />
          <div style={styles.hint}>
            Your public profile will be at /u/{username || 'yourname'}
          </div>
        </div>

        <div style={styles.field}>
          <label style={styles.label}>CONTACT EMAIL (private)</label>
          <input
            style={styles.input}
            type="email"
            value={contactEmail}
            onChange={e => setContactEmail(e.target.value)}
            placeholder="your@email.com"
          />
          <div style={styles.hint}>
            Never shown on your profile. Used only for Marketplace sale emails.
          </div>
        </div>

        <div style={styles.field}>
          <label style={styles.label}>PROFILE VISIBILITY</label>
          <div style={styles.toggleRow}>
            <button
              style={isPublic ? { ...styles.toggleBtn, ...styles.toggleBtnActive } : styles.toggleBtn}
              onClick={() => setIsPublic(true)}
            >PUBLIC</button>
            <button
              style={!isPublic ? { ...styles.toggleBtn, ...styles.toggleBtnActiveRed } : styles.toggleBtn}
              onClick={() => setIsPublic(false)}
            >PRIVATE</button>
          </div>
          <div style={styles.hint}>
            {isPublic
              ? 'Anyone with your link can view your collection, wishlist, and trade/sale list.'
              : 'Only you can see your collection.'}
          </div>
        </div>

        {error && <div style={styles.msgError}>{error}</div>}

        <button style={styles.btn} onClick={handleSubmit} disabled={loading}>
          {loading ? 'SAVING...' : 'CREATE PROFILE'}
        </button>
      </div>
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', fontFamily: "'Rajdhani', sans-serif", position: 'relative' },
  stars: {
    position: 'fixed', inset: 0, opacity: 'var(--stars-opacity)',
    backgroundImage: `
      radial-gradient(1px 1px at 10% 20%, rgba(255,255,255,0.6) 0%, transparent 100%),
      radial-gradient(1px 1px at 30% 50%, rgba(255,255,255,0.4) 0%, transparent 100%),
      radial-gradient(1.5px 1.5px at 50% 10%, rgba(255,255,255,0.7) 0%, transparent 100%),
      radial-gradient(1px 1px at 70% 70%, rgba(255,255,255,0.5) 0%, transparent 100%)
    `,
    pointerEvents: 'none',
  },
  card: { position: 'relative', width: '100%', maxWidth: '480px', background: 'var(--panel-deep)', border: '1px solid var(--border)', borderRadius: '4px', padding: '40px', boxShadow: '0 0 60px rgba(var(--cyan-rgb),0.06)' },
  logoLine: { fontFamily: "'Orbitron', monospace", fontSize: '9px', letterSpacing: '6px', color: 'var(--brand-gold)', textAlign: 'center', marginBottom: '12px', opacity: 0.8 },
  title: { fontFamily: "'Orbitron', monospace", fontSize: '24px', fontWeight: 900, textAlign: 'center', color: 'var(--text-strong)', lineHeight: 1.1, textShadow: '0 0 40px rgba(var(--cyan-rgb),0.3)', margin: 0 },
  accent: { color: 'var(--gold)' },
  subtitle: { fontFamily: "'Share Tech Mono', monospace", fontSize: '11px', color: 'var(--muted-2)', textAlign: 'center', letterSpacing: '3px', marginTop: '6px' },
  divider: { height: '1px', background: 'linear-gradient(90deg, transparent, var(--border), var(--brand-gold), var(--border), transparent)', margin: '24px 0' },
  field: { marginBottom: '20px' },
  label: { display: 'block', fontFamily: "'Orbitron', monospace", fontSize: '9px', letterSpacing: '2px', color: 'var(--muted-2)', marginBottom: '6px' },
  input: { width: '100%', background: 'var(--panel)', border: '1px solid var(--border)', color: 'var(--text)', fontFamily: "'Share Tech Mono', monospace", fontSize: '14px', padding: '10px 14px', borderRadius: '2px', outline: 'none', boxSizing: 'border-box' },
  hint: { fontFamily: "'Share Tech Mono', monospace", fontSize: '10px', color: 'var(--muted-2)', marginTop: '6px' },
  toggleRow: { display: 'flex', gap: '8px', marginBottom: '6px' },
  toggleBtn: { flex: 1, fontFamily: "'Orbitron', monospace", fontSize: '9px', letterSpacing: '1px', padding: '9px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--muted-2)', cursor: 'pointer', borderRadius: '2px' },
  toggleBtnActive: { borderColor: 'var(--green)', color: 'var(--green)', background: 'rgba(var(--green-rgb),0.05)' },
  toggleBtnActiveRed: { borderColor: 'var(--red)', color: 'var(--red)', background: 'rgba(var(--red-rgb),0.05)' },
  btn: { width: '100%', fontFamily: "'Orbitron', monospace", fontSize: '11px', letterSpacing: '2px', padding: '13px', background: 'linear-gradient(135deg, rgba(var(--cyan-rgb),0.15), rgba(var(--brand-gold-rgb),0.1))', border: '1px solid var(--cyan)', color: 'var(--cyan)', cursor: 'pointer', borderRadius: '2px', marginTop: '8px' },
  msgError: { background: 'rgba(var(--red-rgb),0.1)', border: '1px solid rgba(var(--red-rgb),0.3)', color: 'var(--red)', fontFamily: "'Share Tech Mono', monospace", fontSize: '11px', padding: '10px 14px', borderRadius: '2px', marginBottom: '12px', lineHeight: 1.5 },
};
